import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  KeyRound, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Trash2, ChevronDown, ChevronUp,
} from 'lucide-react';

/**
 * Emergent Universal Key override panel.
 *
 * Why it exists
 * -------------
 * The cloud build ships an Emergent key in backend/.env. The offline desktop
 * installer ships the SAME key (injected by GH Actions from a repo secret).
 * Some operators want to plug in their OWN Universal Key — for billing
 * isolation, custom budgets, or because their bundled key has expired. This
 * card lets a Settings admin paste a key that overrides both sources at
 * AI-call time, with no backend restart.
 *
 * Backend endpoints:
 *   GET    /api/settings/emergent-key            → status (masked)
 *   PUT    /api/settings/emergent-key            → save / clear (api_key="")
 *   POST   /api/settings/emergent-key/test       → round-trip a tiny prompt
 */
export default function EmergentKeyOverrideCard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [draftKey, setDraftKey] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings/emergent-key');
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !status) loadStatus();
  }, [open, status, loadStatus]);

  const handleSave = async () => {
    const trimmed = (draftKey || '').trim();
    if (trimmed && !trimmed.toLowerCase().startsWith('sk-')) {
      toast.error("Universal keys start with 'sk-'");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/settings/emergent-key', { api_key: trimmed });
      setStatus(res.data);
      setDraftKey('');
      setTestResult(null);
      toast.success(trimmed ? 'Custom Emergent key saved.' : 'Custom key cleared. Falling back to bundled key.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save key.');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      const res = await api.put('/settings/emergent-key', { api_key: '' });
      setStatus(res.data);
      setDraftKey('');
      setTestResult(null);
      toast.success('Custom key cleared. Falling back to bundled key.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not clear key.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // If the user typed something, test that draft. Otherwise test whatever
      // is currently active (override or env).
      const body = draftKey.trim() ? { api_key: draftKey.trim() } : {};
      const res = await api.post('/settings/emergent-key/test', body);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err?.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const sourceLabel = (() => {
    if (!status) return null;
    if (status.active_source === 'override') return { label: 'Custom override', tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' };
    if (status.active_source === 'env') return { label: 'Bundled (default)', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' };
    return { label: 'No key configured', tone: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' };
  })();

  return (
    <div className="rounded-lg border" data-testid="emergent-key-override-card">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40 transition-colors"
        data-testid="emergent-key-toggle"
      >
        <div className="flex items-center gap-2 text-left">
          <KeyRound className="h-4 w-4 text-primary" />
          <div>
            <p className="text-sm font-medium">Custom Universal Key (advanced)</p>
            <p className="text-xs text-muted-foreground">
              Override the bundled Emergent key. Useful for offline installs.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {sourceLabel && (
            <Badge variant="outline" className={`text-[10px] ${sourceLabel.tone} border-transparent`} data-testid="emergent-key-source-badge">
              {sourceLabel.label}
            </Badge>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading current status…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Active key</p>
                <p className="text-sm font-mono mt-1 break-all" data-testid="emergent-key-active-masked">
                  {status?.active_masked || '— none —'}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Source: <span className="font-medium">{status?.active_source || 'none'}</span>
                </p>
              </div>
              <div className="rounded border p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Bundled fallback</p>
                <p className="text-sm font-mono mt-1 break-all">
                  {status?.env_set ? status.env_masked : '— missing —'}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Shipped via the installer&apos;s <code>resources/backend/.env</code>.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="emergent-key-input">New Universal Key</Label>
            <Input
              id="emergent-key-input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="sk-emergent-…"
              value={draftKey}
              onChange={e => { setDraftKey(e.target.value); setTestResult(null); }}
              data-testid="emergent-key-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Get your key from your Emergent dashboard → Profile → Universal Key. Leave blank to keep the bundled key.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !draftKey.trim()}
              data-testid="emergent-key-save"
              className="gap-1.5"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
              Save override
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleTest}
              disabled={testing}
              data-testid="emergent-key-test"
              className="gap-1.5"
            >
              {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {draftKey.trim() ? 'Test this key' : 'Test active key'}
            </Button>
            {status?.override_set && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClear}
                disabled={saving}
                data-testid="emergent-key-clear"
                className="gap-1.5 text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-3 w-3" /> Clear override
              </Button>
            )}
            {testResult && (
              <div
                className={`flex items-center gap-1.5 text-xs ${testResult.success ? 'text-emerald-600' : 'text-red-600'}`}
                data-testid="emergent-key-test-result"
              >
                {testResult.success ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
